// src/lib/selectClientInfo.js

function normName(raw) {
  if (raw == null) return "";
  let s = String(raw)
    .normalize("NFD").replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const bad = [
    "lda","s a","sa","unipessoal","unip","ltda","ltd","me","eireli",
    "gmbh","srl","spa","ag","inc","co","corp","limited","company","llc"
  ];
  const filtered = s.split(" ").filter(p => !bad.includes(p));
  return filtered.join(" ").trim() || s;
}

const pick = (o, ks, fb = "") => {
  for (const k of ks) {
    const v = o?.[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return fb;
};

const hasDetails = (c) =>
  !!c && (
    c.address || c.city || c.postalCode || c.country ||
    c.email || c.phone || c.contactEmail || c.phoneNumber
  );

// Preferência de chaves para contacto
const EMAIL_KEYS = ["contactEmail", "email", "companyEmail", "company_email"];
const PHONE_KEYS = [
  "phoneNumber", "phone", "phone_number",
  "companyPhoneNumber", "company_phone_number", "company_phone",
  "contactPhone"
];

/**
 * Tenta construir um objeto de cliente completo a partir do cache `clients`.
 * Prioriza contactEmail/phoneNumber, mas garante que ambas variantes
 * (email/contactEmail e phone/phoneNumber) são sempre preenchidas.
 */
export function selectClientInfo(invoice = {}, clients = []) {
  const list = Array.isArray(clients) ? clients : [];

  // 0) Short-circuit: objecto client já detalhado no invoice
  if (invoice?.client && typeof invoice.client === "object" && hasDetails(invoice.client)) {
    const c = invoice.client;
    const emailPick = pick(c, EMAIL_KEYS);
    const phonePick = pick(c, PHONE_KEYS);
    return {
      name: pick(c, ["name","companyName"], invoice.clientName || "—"),
      address: pick(c, ["address","address1","street","streetAddress","street_address"]),
      postalCode: pick(c, ["postalCode","postal_code","zip","zipCode","postcode"]),
      city: pick(c, ["city","town","locality"]),
      country: pick(c, ["country","country_name","countryName"]),
      // garantir ambas as variantes
      email: emailPick,
      contactEmail: emailPick,
      phone: phonePick,
      phoneNumber: phonePick,
    };
  }

  const srcId = invoice?.clientId ?? invoice?.client?.id ?? null;
  const srcName = (invoice?.clientName ?? invoice?.client?.name ?? invoice?.client?.companyName ?? "").trim();

  // 1) Procura por ID
  if (srcId != null) {
    const hit = list.find(c => String(c.id) === String(srcId));
    if (hit) {
      const emailPick = pick(hit, EMAIL_KEYS);
      const phonePick = pick(hit, PHONE_KEYS);
      return {
        name: pick(hit, ["name","companyName"], srcName || "—"),
        address: pick(hit, ["address","address1","street","streetAddress","street_address"]),
        postalCode: pick(hit, ["postalCode","postal_code","zip","zipCode","postcode"]),
        city: pick(hit, ["city","town","locality"]),
        country: pick(hit, ["country","country_name","countryName"]),
        email: emailPick,
        contactEmail: emailPick,
        phone: phonePick,
        phoneNumber: phonePick,
      };
    }
  }

  // 2) Procura por nome normalizado
  if (srcName) {
    const target = normName(srcName);
    const hit = list.find(c => normName(c.name ?? c.companyName) === target);
    if (hit) {
      const emailPick = pick(hit, EMAIL_KEYS);
      const phonePick = pick(hit, PHONE_KEYS);
      return {
        name: pick(hit, ["name","companyName"], srcName),
        address: pick(hit, ["address","address1","street","streetAddress","street_address"]),
        postalCode: pick(hit, ["postalCode","postal_code","zip","zipCode","postcode"]),
        city: pick(hit, ["city","town","locality"]),
        country: pick(hit, ["country","country_name","countryName"]),
        email: emailPick,
        contactEmail: emailPick,
        phone: phonePick,
        phoneNumber: phonePick,
      };
    }
  }

  // 3) Fallback: devolve pelo menos o nome (e chaves vazias para consistência)
  return {
    name: srcName || (srcId != null ? String(srcId) : "—"),
    address: "",
    postalCode: "",
    city: "",
    country: "",
    email: "",
    contactEmail: "",
    phone: "",
    phoneNumber: "",
  };
}

export { normName };
export default selectClientInfo;
